// HTTP client functions for the Value Calculator backend API.
//
// Each function takes `apiFetch` as its first parameter (the throwing mini-app
// boundary from `_shared/hooks/useApi.ts`). Reads go through `fetchJson`, which
// parses the 2xx body with the wire schema shared with api.ts (`./wire`).

import { fetchJson } from '../_shared/fetch-json';
import {
  CatalogResponseSchema,
  MetricHistoryResponseSchema,
  OwnedAccountsResponseSchema,
  SnapshotsResponseSchema,
  type CatalogResponse,
  type MetricHistoryResponse,
  type OwnedAccountsResponse,
  type SnapshotsResponse,
} from './wire';

export type {
  CatalogDefinition,
  CatalogResponse,
  MetricHistoryResponse,
  OwnedAccount,
  OwnedAccountLatestSnapshot,
  OwnedAccountOpenOpportunity,
  OwnedAccountsResponse,
  SnapshotDetail,
  SnapshotMetric,
  SnapshotsResponse,
  SnapshotSummary,
} from './wire';

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

// ── Save body ─────────────────────────────────────────────────────────────────

// Capture type is deliberately absent: the server infers it per save (api.ts).
export interface SaveSnapshotBody {
  accountId: string;
  opportunityId?: string;
  customerName: string;
  storySnapshot: Record<string, unknown>;
  totalAnnualValue: number;
  headlineSummary: string;
  observations: Array<{ metricKey: string; value: number; source: string }>;
}

// ── API functions ─────────────────────────────────────────────────────────────

export function fetchSnapshots(apiFetch: ApiFetch, accountId: string): Promise<SnapshotsResponse> {
  return fetchJson(
    apiFetch,
    SnapshotsResponseSchema,
    `/api/tools/value-calculator/snapshots?accountId=${encodeURIComponent(accountId)}`,
  );
}

/**
 * Autosave push. Resolves once the server answered 2xx; the response body is deliberately
 * not read. The Salesforce upsert has already committed by then, so a malformed or cut-off
 * body must not be reported as a failed save (that would leave the state dirty and re-push
 * every autosave interval). `apiFetch` throws on non-2xx.
 */
export async function saveSnapshot(apiFetch: ApiFetch, body: SaveSnapshotBody): Promise<void> {
  await apiFetch('/api/tools/value-calculator/snapshots', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function fetchMetricHistory(apiFetch: ApiFetch, accountId: string): Promise<MetricHistoryResponse> {
  return fetchJson(
    apiFetch,
    MetricHistoryResponseSchema,
    `/api/tools/value-calculator/metric-history?accountId=${encodeURIComponent(accountId)}`,
  );
}

export function fetchCatalog(apiFetch: ApiFetch): Promise<CatalogResponse> {
  return fetchJson(apiFetch, CatalogResponseSchema, '/api/tools/value-calculator/catalog');
}

export function fetchOwnedAccounts(apiFetch: ApiFetch): Promise<OwnedAccountsResponse> {
  return fetchJson(apiFetch, OwnedAccountsResponseSchema, '/api/tools/value-calculator/accounts');
}
